import React from "react";

const ENABLED_CHANGE_EVENT =
  "chinook-edge-mentor-enabled-change";

const normalizeModuleKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "default";

export const getEdgeMentorStorageKey = (moduleKey) =>
  `edgeMentor.${normalizeModuleKey(moduleKey)}.enabled`;

const readEnabledState = (
  storageKey,
  defaultEnabled,
) => {
  if (typeof window === "undefined") {
    return Boolean(defaultEnabled);
  }

  try {
    const storedValue =
      window.localStorage.getItem(storageKey);

    return storedValue === null
      ? Boolean(defaultEnabled)
      : storedValue === "true";
  } catch (error) {
    console.warn(
      "[Edge Mentor] Could not read enabled state:",
      error,
    );

    return Boolean(defaultEnabled);
  }
};

/**
 * Universal per-module Edge Mentor state.
 *
 * Examples:
 *   useEdgeMentorEnabled("pv")
 *   useEdgeMentorEnabled("piping")
 *   useEdgeMentorEnabled("structural")
 *
 * Storage stays independent:
 *   edgeMentor.pv.enabled
 *   edgeMentor.piping.enabled
 *   edgeMentor.structural.enabled
 *
 * This hook also synchronizes all mounted consumers immediately, which lets
 * the global navbar icon control a module-specific EdgeMentorHost elsewhere
 * in the React tree.
 */
export const useEdgeMentorEnabled = (
  moduleKey,
  defaultEnabled = true,
) => {
  const normalizedModuleKey =
    React.useMemo(
      () => normalizeModuleKey(moduleKey),
      [moduleKey],
    );

  const storageKey = React.useMemo(
    () =>
      getEdgeMentorStorageKey(
        normalizedModuleKey,
      ),
    [normalizedModuleKey],
  );

  const [enabled, setEnabledState] =
    React.useState(() =>
      readEnabledState(
        storageKey,
        defaultEnabled,
      ),
    );

  // If a future universal navbar switches from PV -> Piping -> Structural,
  // reload the correct independent state for the newly active module.
  React.useEffect(() => {
    setEnabledState(
      readEnabledState(
        storageKey,
        defaultEnabled,
      ),
    );
  }, [storageKey, defaultEnabled]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleEnabledChange = (event) => {
      const detail = event?.detail;

      if (
        detail?.storageKey !== storageKey
      ) {
        return;
      }

      setEnabledState(
        Boolean(detail.enabled),
      );
    };

    const handleStorageChange = (event) => {
      if (event.key !== storageKey) {
        return;
      }

      setEnabledState(
        event.newValue === null
          ? Boolean(defaultEnabled)
          : event.newValue === "true",
      );
    };

    window.addEventListener(
      ENABLED_CHANGE_EVENT,
      handleEnabledChange,
    );

    window.addEventListener(
      "storage",
      handleStorageChange,
    );

    return () => {
      window.removeEventListener(
        ENABLED_CHANGE_EVENT,
        handleEnabledChange,
      );

      window.removeEventListener(
        "storage",
        handleStorageChange,
      );
    };
  }, [storageKey, defaultEnabled]);

  const setEnabled = React.useCallback(
    (nextEnabled) => {
      const nextValue =
        Boolean(nextEnabled);

      setEnabledState(nextValue);

      if (typeof window === "undefined") {
        return;
      }

      try {
        window.localStorage.setItem(
          storageKey,
          String(nextValue),
        );
      } catch (error) {
        console.warn(
          `[Edge Mentor] Could not save ${normalizedModuleKey} enabled state:`,
          error,
        );
      }

      window.dispatchEvent(
        new CustomEvent(
          ENABLED_CHANGE_EVENT,
          {
            detail: {
              moduleKey:
                normalizedModuleKey,
              storageKey,
              enabled: nextValue,
            },
          },
        ),
      );
    },
    [normalizedModuleKey, storageKey],
  );

  const toggleEnabled =
    React.useCallback(() => {
      setEnabled(!enabled);
    }, [enabled, setEnabled]);

  return {
    enabled,
    setEnabled,
    toggleEnabled,
    storageKey,
    moduleKey: normalizedModuleKey,
  };
};

// This file now owns shared state only.
// The visible global icon lives in EdgeMentorNavbarToggle.jsx.
export default useEdgeMentorEnabled;
